import {
  useEffect,
  useState,
  useRef,
  type MutableRefObject,
  type ChangeEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowDropright, IoMdArrowBack } from 'react-icons/io';
import { IoIosSearch } from 'react-icons/io';

import { fetchPrograms, FETCH_PROGRAM_WITH_ID_SUCCESS } from '../../redux/programActions';
import { fetchPrincipalWithId } from '../../redux/principalActions';

type RootState = any;

function ProgramPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { program_list, loading } = useSelector((state: RootState) => state.program);
  const { principal } = useSelector((state: RootState) => state.principal);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setSearchVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) return;
    // @ts-expect-error thunk
    dispatch(fetchPrograms(id));
    if (!(principal && principal.principalId)) {
      // @ts-expect-error thunk
      dispatch(fetchPrincipalWithId(id));
    }
  }, [dispatch, id, principal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !target?.closest('button[aria-label="Toggle search"]')
      ) {
        setSearchVisible(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = async (item: {
    programId: number;
    programName?: string;
    displayName?: string;
    processorType?: string | number;
    'processor.processorId'?: string | number;
  }) => {
    try {
      dispatch({
        type: FETCH_PROGRAM_WITH_ID_SUCCESS,
        payload: {
          programName: item.programName ?? item.displayName,
          displayName: item.displayName,
          principalId: id,
          programId: item.programId,
          processorType: item.processorType,
          'processor.processorId': item['processor.processorId'],
        },
      });
      navigate(`/program/${item.programId}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredPrograms =
    program_list?.rows?.filter((item: any) =>
      item.programName.toLowerCase().includes(searchTerm.toLowerCase()),
    ) ?? [];

  const toggleSearchVisibility = () => {
    setSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchTerm('');
    }
  };

  return (
    <div className="h-screen bg-white overflow-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {program_list && principal && principal.principalId && (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="h-16 flex items-center">
                  <button
                    type="button"
                    onClick={() => navigate('/principal')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <IoMdArrowBack className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    {!isSearchVisible && (
                      <div className="text-center">
                        <h1 className="text-lg font-semibold text-gray-900">Programs</h1>
                        <div className="text-sm text-gray-500">{principal.name}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      ref={searchRef as MutableRefObject<HTMLDivElement | null>}
                      className={`overflow-hidden transition-all duration-300 ease-in-out flex items-center ${
                        isSearchVisible ? 'flex-1 max-w-xs' : 'w-0'
                      }`}
                    >
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search programs..."
                        disabled={!isSearchVisible}
                        autoFocus={isSearchVisible}
                        className={`w-full h-10 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all duration-300 bg-gray-50 ${
                          isSearchVisible
                            ? 'opacity-100 translate-x-0 pointer-events-auto'
                            : 'opacity-0 -translate-x-full pointer-events-none'
                        }`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={toggleSearchVisibility}
                      aria-label="Toggle search"
                      className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
                    >
                      <IoIosSearch className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50/80">
            <div className="p-4 sm:p-6">
              <div className="max-w-5xl mx-auto space-y-3">
                {filteredPrograms.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <IoIosSearch className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No programs found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {searchTerm ? 'Try a different search term' : 'No programs to display'}
                    </p>
                  </div>
                ) : (
                  filteredPrograms.map((item: any, index: number) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleClick(item)}
                      className="w-full flex items-center justify-between px-6 py-4 text-left bg-white rounded-xl transition-all shadow-soft hover:shadow-card-hover border border-gray-100 active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-4">
                        {item.logo && (
                          <img
                            src={item.logo}
                            alt={item.programName}
                            className="w-14 h-12 object-contain rounded-lg bg-gray-50 p-1"
                          />
                        )}
                        <span className="text-gray-900 font-medium">{item.programName}</span>
                      </div>
                      <IoMdArrowDropright className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgramPage;

